import axios from "axios";
import { IHttpInput } from "../../types";
import { Crypto } from "../../utils/encryption";
import { handleResponse, handleError } from "../../utils/response";

//init token from cookie here
const baseURL = process.env.REACT_APP_API;

let instance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "multipart/form-data",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});
const baseName = process.env.REACT_APP_BASENAME ? process.env.REACT_APP_BASENAME : '';

// Load whitelist from environment variable (comma-separated URLs)
const whitelistEnv = process.env.REACT_APP_API_WHITELIST || '';
const whitelistUrl = whitelistEnv.split(',').map(url => url.trim()).filter(url => url);

// Improved URL validation function to prevent SSRF attacks
const validateUrl = (fullUrl: string): boolean => {
  try {
    const url = new URL(fullUrl);
    // Match protocol, host, and path prefix exactly
    return whitelistUrl.some(allowedUrl => {
      try {
        const allowed = new URL(allowedUrl);
        return (
          url.protocol === allowed.protocol &&
          url.host === allowed.host &&
          fullUrl.startsWith(allowedUrl)
        );
      } catch (e) {
        return false;
      }
    });
  } catch (error) {
    // Invalid URL format
    return false;
  }
};

const getAll = async <T>(url: string, input: IHttpInput) => {
  let passToken = { Authorization: `` };
  const fullUrl = `${baseURL}${url}`;
  
  // Validate URL against whitelist to prevent SSRF
  if (!validateUrl(fullUrl)) {
    console.warn(`Blocked SSRF attempt: ${fullUrl}`);
    throw new Error('URL not allowed - potential SSRF attack blocked');
  }
  
  try {
    const resp = await axios.get<T>(fullUrl, {headers: passToken})
    return handleResponse(resp)
  } catch (e){
    if (e instanceof Error) {
      console.log(`Error: ${e}`)
      handleError(e)
    } 
  }
};

const getSingle = async <T>(url: string, input: IHttpInput) => {
  let passToken = { Authorization: `` };
  var encryptInput = "";
  if (input.id != null) {
    encryptInput = "/" + Crypto.encrypt(input.id);
  }
  const fullUrl = `${baseURL}${url}${encryptInput}`;
  // Validate URL against whitelist to prevent SSRF
  if (!validateUrl(fullUrl)) {
    throw new Error('URL not allowed - potential SSRF attack blocked');
  }

  try {
    const resp = await axios.get<T>(fullUrl, {headers: passToken})
    return handleResponse(resp)
  } catch (e){
    if (e instanceof Error) {
      console.log(`Error: ${e}`)
      handleError(e)
    } 
  }
};
const post = async <T>(url: string, input: IHttpInput) => {
  let passToken = { Authorization: `` };
  const fullUrl = `${baseURL}${url}`;

  // Validate URL against whitelist to prevent SSRF
  if (!validateUrl(fullUrl)) {
    throw new Error('URL not allowed - potential SSRF attack blocked');
  }
  
  try {
    const encryptBody = Crypto.encrypt(input.body)
    const resp = await axios.post<T>(fullUrl, encryptBody, {headers: passToken})
    return handleResponse(resp)

  } catch (e){
    if (e instanceof Error) {
      console.log(`Error: ${e}`)
      handleError(e)
    } 
  }
};

const put = async <T>(url: string, input: IHttpInput) => {
  let passToken = { Authorization: `` };
  const fullUrl = `${baseURL}${url}`;
  
  // Validate URL against whitelist to prevent SSRF
  if (!validateUrl(fullUrl)) {
    throw new Error('URL not allowed - potential SSRF attack blocked');
  }

  try {
    const encryptBody = Crypto.encrypt(input.body)
    const resp = await axios.put<T>(fullUrl, encryptBody, {headers: passToken})
    return handleResponse(resp)

  } catch (e){
    if (e instanceof Error) {
      console.log(`Error: ${e}`)
      handleError(e)
    } 
  }
};

const remove = async <T>(url: string, input: IHttpInput) => {
  let passToken = { Authorization: `` };
  const fullUrl = `${baseURL}${url}/${Crypto.encrypt(input.id)}`;
  
  // Validate URL against whitelist to prevent SSRF
  if (!validateUrl(fullUrl)) {
    throw new Error('URL not allowed - potential SSRF attack blocked');
  }

  try {
    const resp = await axios.delete<T>(fullUrl)
    return handleResponse(resp)

  } catch (e){
    if (e instanceof Error) {
      console.log(`Error: ${e}`)
      handleError(e)
    } 
  }
};

export const apiProvider = {
  getAll,
  getSingle,
  post,
  put,
  remove,
};
